/**
 * Top Committee Clients on the vendor detail page (sites#261): one row shape
 * for the ranked list (vendorClients, rundeck#771 PAYS rank) and the live
 * connection it replaces, and the Sankey nodes/links built from those rows.
 * No ECharts here: the chart comes from /js/money-flow.js, loaded on demand.
 */

import type { FlowLink, FlowNode } from './flow-types';

export interface ClientRow {
  id: string;
  name: string | null;
  committeeType: string | null;
  party: string | null;
  affinityDem: number | null;
  affinityRep: number | null;
  amount: number | null;
  count: number | null;
  purposes: string | null;
}

export interface ClientList {
  rows: ClientRow[];
  /** P in "top N of P"; null when unknown. */
  total: number | null;
  /** Clients beyond the rows and their amount, for the Sankey's Other node. */
  otherCount: number;
  otherAmount: number;
}

export const VENDOR_RANKED_CLIENTS_QUERY = `
  query VendorRankedClients($id: String!, $cycle: Int) {
    vendorClients(vendorId: $id, cycle: $cycle) {
      ranked clientCount totalReceived otherCount otherAmount
      clients { committeeId name committeeType party affinityDem affinityRep amount count purposes rank }
    }
  }
`;

interface RankedClient {
  committeeId: string;
  name: string | null;
  committeeType: string | null;
  party: string | null;
  affinityDem: number | null;
  affinityRep: number | null;
  amount: number | null;
  count: number | null;
  purposes: string | null;
}

export interface RankedClientsData {
  vendorClients: {
    ranked: boolean | null;
    clientCount: number | null;
    totalReceived: number | null;
    otherCount: number | null;
    otherAmount: number | null;
    clients: RankedClient[] | null;
  } | null;
}

/** The ranked list, or null when the graph has none (the page then keeps its live list). */
export function rankedClientList(data: RankedClientsData | null | undefined): ClientList | null {
  const v = data?.vendorClients;
  if (!v?.ranked || !v.clients?.length) return null;
  return {
    rows: v.clients.map((c) => ({
      id: c.committeeId,
      name: c.name,
      committeeType: c.committeeType,
      party: c.party,
      affinityDem: c.affinityDem,
      affinityRep: c.affinityRep,
      amount: c.amount,
      count: c.count,
      purposes: c.purposes,
    })),
    total: v.clientCount,
    otherCount: Number(v.otherCount) || 0,
    otherAmount: Number(v.otherAmount) || 0,
  };
}

/** The live connection's rows; P and Other from the vendor's all-cycles totals. */
export function liveClientList(rows: ClientRow[], clientCount: unknown, totalReceived: unknown): ClientList {
  const total = clientCount == null || clientCount === '' ? null : Number(clientCount);
  const otherCount = total != null && Number.isFinite(total) ? Math.max(0, total - rows.length) : 0;
  const shown = rows.reduce((t, r) => t + (Number(r.amount) || 0), 0);
  const received = Number(totalReceived);
  const otherAmount = otherCount > 0 && Number.isFinite(received) && received - shown > 0 ? received - shown : 0;
  return { rows, total: total != null && Number.isFinite(total) ? total : null, otherCount, otherAmount };
}

/** Committees on the left, the vendor on the right, and an Other node for the remainder. */
export function vendorFlow(vendor: { id: string; name: string }, list: ClientList): { nodes: FlowNode[]; links: FlowLink[] } {
  const focal: FlowNode = {
    id: 'focal', side: 'focal', kind: 'Vendor', entityId: vendor.id, fecId: null, name: vendor.name,
    party: null, affinityDem: null, affinityRep: null, amount: null, count: null, otherCount: null,
  };
  const nodes: FlowNode[] = [focal];
  const links: FlowLink[] = [];
  for (const r of list.rows) {
    const amount = Number(r.amount);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const id = `in:${r.id}`;
    nodes.push({
      id, side: 'in', kind: 'Committee', entityId: r.id, fecId: null, name: r.name || 'Unknown committee',
      party: r.party, affinityDem: r.affinityDem, affinityRep: r.affinityRep, amount, count: r.count, otherCount: null,
    });
    links.push({ source: id, target: focal.id, amount, count: r.count, flow: 'payment' });
  }
  if (links.length && list.otherCount > 0 && list.otherAmount > 0) {
    nodes.push({
      id: 'in:other', side: 'in', kind: 'Other', entityId: null, fecId: null,
      name: `Other committees (${list.otherCount.toLocaleString('en-US')})`,
      party: null, affinityDem: null, affinityRep: null, amount: list.otherAmount, count: null, otherCount: list.otherCount,
    });
    links.push({ source: 'in:other', target: focal.id, amount: list.otherAmount, count: null, flow: 'payment' });
  }
  return { nodes, links };
}

const MONEY_FLOW_SRC = '/js/money-flow.js';
let flowScript: Promise<void> | null = null;

/** Load the Sankey bundle once; it registers window.electinfoMoneyFlow. */
export function loadMoneyFlow(doc: Document = document): Promise<void> {
  if (window.electinfoMoneyFlow) return Promise.resolve();
  if (!flowScript) {
    flowScript = new Promise<void>((resolve, reject) => {
      const s = doc.createElement('script');
      s.src = MONEY_FLOW_SRC;
      s.async = true;
      s.onload = () => (window.electinfoMoneyFlow ? resolve() : reject(new Error('money-flow did not register')));
      s.onerror = () => reject(new Error(`failed to load ${MONEY_FLOW_SRC}`));
      doc.head.appendChild(s);
    }).catch((err) => {
      flowScript = null;
      throw err;
    });
  }
  return flowScript;
}
